import React, { useEffect, useMemo, useState } from "react";
import "./Header.css";
import { FaBars, FaTimes, FaPhoneAlt, FaArrowRight } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLocalContext } from "../../context/LocalContext";

const drawerAnim = {
  hidden: { x: 40, opacity: 0 },
  show: { x: 0, opacity: 1, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } },
  exit: { x: 40, opacity: 0, transition: { duration: 0.2 } },
};

const overlayAnim = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { webinfo = {} } = useLocalContext();

  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navItems = useMemo(
    () => [
      { label: "Home", path: "/" },
      { label: "Services", path: "/services" },
      { label: "Our process", path: "/process" },
      { label: "Contact", path: "/contact" },
    ],
    []
  );

  const phoneCall = webinfo?.phonecall || webinfo?.phone || "";
  const phoneText = webinfo?.phone || webinfo?.phonecall || "Call";
  const brand = webinfo?.name || "smartprintersolutions.com";
  const brandMark = (brand?.trim()?.[0] || "S").toUpperCase();
  const isHome = location.pathname === "/";
  const overHero = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`hdr ${scrolled ? "hdr--scrolled" : ""} ${overHero ? "hdr--over-hero" : ""}`}>
      <div className="hdr__row">
        <div
          className="hdr__brand"
          onClick={() => go("/")}
          onKeyDown={(e) => e.key === "Enter" && go("/")}
          role="button"
          tabIndex={0}
          aria-label={`${brand} home`}
        >
          <div className="hdr__mark" aria-hidden="true">
            {brandMark}
          </div>
          <div className="hdr__brandText">
            <p className="hdr__name">{brand}</p>
            <p className="hdr__tagline">Digital marketing · Dallas, TX</p>
          </div>
        </div>

        <div className="hdr__nav" aria-label="Header navigation">
          <nav className="hdr__links" aria-label="Main navigation">
            {navItems.map((it) => (
              <button
                key={it.path}
                type="button"
                className={`hdr__link ${isActive(it.path) ? "hdr__link--active" : ""}`}
                onClick={() => go(it.path)}
              >
                {it.label}
              </button>
            ))}
          </nav>

          <div className="hdr__actions">
            <button
              type="button"
              className="hdr__btn hdr__btn--ghost"
              onClick={() => {
                if (phoneCall) window.location.href = `tel:${phoneCall}`;
                else go("/contact");
              }}
            >
              <FaPhoneAlt className="hdr__btnIcon" aria-hidden />
              <span>{phoneText}</span>
            </button>

            <button type="button" className="hdr__btn hdr__btn--primary" onClick={() => go("/contact")}>
              Get a quote <FaArrowRight className="hdr__btnIcon" aria-hidden />
            </button>
          </div>
        </div>

        <button type="button" className="hdr__burger" onClick={() => setOpen(true)} aria-label="Open menu">
          <FaBars />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="hdr__overlay"
              variants={overlayAnim}
              initial="hidden"
              animate="show"
              exit="exit"
              onClick={() => setOpen(false)}
            />

            <motion.aside
              className="hdr__drawer"
              variants={drawerAnim}
              initial="hidden"
              animate="show"
              exit="exit"
              role="dialog"
              aria-modal="true"
              aria-label="Mobile menu"
            >
              <div className="hdr__drawerTop">
                <div className="hdr__drawerBrand">
                  <div className="hdr__mark hdr__mark--sm" aria-hidden="true">
                    {brandMark}
                  </div>
                  <div>
                    <p className="hdr__drawerTitle">{brand}</p>
                    <p className="hdr__drawerSub">Strategy, campaigns, measurable growth.</p>
                  </div>
                </div>

                <button type="button" className="hdr__close" onClick={() => setOpen(false)} aria-label="Close menu">
                  <FaTimes />
                </button>
              </div>

              <div className="hdr__drawerLinks">
                {navItems.map((it) => (
                  <button
                    key={it.path}
                    type="button"
                    className={`hdr__mLink ${isActive(it.path) ? "hdr__mLink--active" : ""}`}
                    onClick={() => go(it.path)}
                  >
                    <span>{it.label}</span>
                    <span aria-hidden="true">→</span>
                  </button>
                ))}
              </div>

              <div className="hdr__drawerBottom">
                <div className="hdr__help">
                  <b>Response time:</b> We aim to reply within one to two business days. For urgent campaign issues,
                  note your timeline on the contact form.
                </div>

                <div className="hdr__drawerBtns">
                  <button
                    type="button"
                    className="hdr__btn hdr__btn--ghost"
                    onClick={() => {
                      if (phoneCall) window.location.href = `tel:${phoneCall}`;
                      else go("/contact");
                    }}
                  >
                    <FaPhoneAlt /> {phoneText}
                  </button>

                  <button type="button" className="hdr__btn hdr__btn--primary" onClick={() => go("/contact")}>
                    Get a quote <FaArrowRight className="hdr__btnIcon" aria-hidden />
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
